import { useEffect, useRef } from "react";

// web-rnw twin of ./useUndoShortcuts.ts (#336). Ctrl+Z undoes, Ctrl+Shift+Z
// redoes (Cmd on a Mac), on the whole page while the editor is mounted.
// ⛔ Not inside a text field: there the browser's own undo of the typing is
// what the user means, and taking it over would undo a whole edit instead of
// the last characters.
// This package compiles without the DOM lib, so the little of it used here is
// declared locally (same as dismissSoftKeyboard.web.ts).

type KeyTarget = { tagName?: string; isContentEditable?: boolean } | null;
type KeyEvent = {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  target: KeyTarget;
  preventDefault: () => void;
};
declare const window: {
  addEventListener: (type: "keydown", fn: (e: KeyEvent) => void) => void;
  removeEventListener: (type: "keydown", fn: (e: KeyEvent) => void) => void;
};

const isTextTarget = (el: KeyTarget): boolean => {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
};

export function useUndoShortcuts(undo: () => void, redo: () => void): void {
  const ref = useRef({ undo, redo });
  ref.current = { undo, redo };
  useEffect(() => {
    const onKey = (e: KeyEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (e.key.toLowerCase() !== "z" || isTextTarget(e.target)) return;
      e.preventDefault();
      if (e.shiftKey) ref.current.redo();
      else ref.current.undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
